/**
 * /app/$tenantSlug/wfh/$id — WFH request detail (migration 063).
 *
 * Read-only detail with the lifecycle fields (the request's own status +
 * decision fields ARE the timeline; there is no separate timeline table,
 * mirroring leave). Cancel is available to the requester for a
 * pending/approved request.
 */

import { createFileRoute, useParams } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useWFHRequest, useCancelWFHRequest } from "@/hooks/use-wfh";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/wfh/$id")({
  component: WFHDetailRoute,
});

function WFHDetailRoute() {
  const { id } = useParams({ from: "/_authgated/app/$tenantSlug/wfh/$id" });
  const numId = Number(id);
  const q = useWFHRequest(numId);
  const cancel = useCancelWFHRequest();

  return (
    <TimeAndLeaveShell title="WFH request" description="">
      <LeaveFeatureGate>
        {q.isLoading && <Skeleton className="h-40 w-full max-w-2xl" />}
        {q.data && (
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {q.data.start_date} → {q.data.end_date}
                <Badge variant="outline">{q.data.status}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Requested by: </span>
                {q.data.user_name ?? `User #${q.data.user_id}`}
              </p>
              <p>
                <span className="text-muted-foreground">Working days: </span>
                {q.data.total_days}{" "}
                <span className="text-muted-foreground">
                  (no leave balance used)
                </span>
              </p>
              {q.data.reason && (
                <p>
                  <span className="text-muted-foreground">Reason: </span>
                  {q.data.reason}
                </p>
              )}
              {q.data.decided_by_id && (
                <p>
                  <span className="text-muted-foreground">Decided by: </span>
                  {q.data.decided_by_name ?? `User #${q.data.decided_by_id}`}
                </p>
              )}
              {q.data.decision_notes && (
                <p>
                  <span className="text-muted-foreground">Notes: </span>
                  {q.data.decision_notes}
                </p>
              )}
              {(q.data.status === "pending" ||
                q.data.status === "approved") && (
                <div className="pt-3">
                  <Button
                    variant="outline"
                    disabled={cancel.isPending}
                    onClick={async () => {
                      try {
                        await cancel.mutateAsync(numId);
                        toast.success("WFH request cancelled");
                      } catch {
                        toast.error("Failed to cancel");
                      }
                    }}
                  >
                    Cancel request
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
